import React, { useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { InvestorPortalHeader } from './InvestorPortalHeader';
import { InvestorPortalNav } from './InvestorPortalNav';
import { InvestorPreviewBanner } from './InvestorPreviewBanner';
import { useIdleTimeout } from '../../hooks/investor-relations/useIdleTimeout';
import { useAuth } from '../../contexts/AuthContext';
import { useInvestorPreview } from '../../contexts/InvestorPreviewContext';

export function InvestorPortalLayout({ children }: { children?: React.ReactNode }) {
  const { logout } = useAuth();
  const { isPreviewing } = useInvestorPreview();

  // Auto-logout after 30 minutes of inactivity. Investor sessions are
  // higher-sensitivity than standard employee sessions — enforce idle timeout.
  //
  // Not during a preview: the session behind it belongs to an admin who is
  // checking the portal, and signing them out of OfficeHub because a preview
  // tab sat idle is a bug, not a security win.
  const handleIdleTimeout = useCallback(() => {
    if (isPreviewing) return;
    logout().catch(() => {
      // Force browser to login page if Firebase signout fails
      window.location.href = '/login';
    });
  }, [logout, isPreviewing]);

  useIdleTimeout(handleIdleTimeout);

  return (
    <div className="min-h-screen bg-slate-50">
      <InvestorPreviewBanner />
      <InvestorPortalHeader />
      <div className="flex">
        <InvestorPortalNav />
        <main className="flex-1 p-6 min-w-0">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
